const sizes = {
  sm: '(max-width: 600px)',
  md: '(max-width: 900px)',
};

const generatorStyles = {
  Generator: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: '#fff',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: '0.25rem',
    padding: '0.4rem 1.25rem',
    borderBottom: '1px solid rgba(0,0,0,0.08)',
    backgroundColor: '#fff',
    minHeight: '46px',
    boxSizing: 'border-box',
    [`@media ${sizes.sm}`]: { padding: '0.35rem 0.6rem' },
  },
  hint: {
    fontSize: '0.9rem',
    color: '#1a1a1a',
    fontWeight: 400,
    // Hide the long hint on small screens so the toolbar actions stay reachable.
    [`@media ${sizes.md}`]: { display: 'none' },
  },
  toolbarSpacer: { flexGrow: 1 },
  iconGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.15rem',
    [`@media ${sizes.sm}`]: { gap: '0.05rem' },
  },
  toolbarIcon: {
    color: '#1a1a1a',
    padding: '8px',
    [`@media ${sizes.sm}`]: { padding: '5px' },
  },
  toolbarTextBtn: {
    color: '#1a1a1a',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.9rem',
  },
  // Heart "Save" button: full text on desktop, icon-only on small screens so
  // the toolbar icon row never overflows.
  saveButton: {
    color: '#1a1a1a',
    textTransform: 'none',
    fontWeight: 600,
    [`@media ${sizes.sm}`]: {
      minWidth: 'auto',
      padding: '5px',
      '& $saveLabel': { display: 'none' },
      '& .MuiButton-startIcon': { marginRight: 0, marginLeft: 0 },
    },
  },
  saveLabel: {},
  a11yChip: {
    fontWeight: 600,
    marginLeft: '0.5rem',
    [`@media ${sizes.sm}`]: { display: 'none' },
  },
  // Wrapper that positions the swatch columns and the "add between" controls.
  colorsWrap: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    overflow: 'hidden',
    [`@media ${sizes.md}`]: { overflow: 'auto' },
  },
  colors: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    overflow: 'hidden',
    [`@media ${sizes.md}`]: {
      flexWrap: 'wrap',
      overflow: 'visible',
    },
  },
  // Thin hover strip sitting on the boundary between two columns.
  adder: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '40px',
    marginLeft: '-20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 20,
    '&:hover $adderButton': {
      opacity: 1,
      transform: 'scale(1)',
    },
    [`@media ${sizes.md}`]: { display: 'none' },
  },
  adderButton: {
    backgroundColor: '#fff',
    color: '#1a1a1a',
    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
    opacity: 0,
    transform: 'scale(0.6)',
    transition: 'opacity 0.15s ease, transform 0.15s ease',
    '&:hover': { backgroundColor: '#fff' },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  // Touch alternative to the spacebar shortcut. A minimalistic circular CTA
  // floating in the middle of the four swatches; hidden on desktop where the
  // spacebar (and the hint) already cover palette generation.
  generateFab: {
    display: 'none',
    [`@media ${sizes.md}`]: {
      display: 'flex',
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 30,
      width: '56px',
      height: '56px',
      backgroundColor: '#fff',
      color: '#1a1a1a',
      boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
      '&:hover': { backgroundColor: '#fff' },
      '&:active': { transform: 'translate(-50%, -50%) scale(0.94)' },
    },
  },

  // Palette history side panel.
  historyPanel: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: '230px',
    backgroundColor: '#fff',
    borderLeft: '1px solid rgba(0,0,0,0.1)',
    boxShadow: '-4px 0 16px rgba(0,0,0,0.08)',
    zIndex: 40,
    display: 'flex',
    flexDirection: 'column',
    [`@media ${sizes.sm}`]: { width: '78%' },
  },
  historyHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.75rem 1rem',
    borderBottom: '1px solid rgba(0,0,0,0.08)',
  },
  historyTitle: {
    fontSize: '0.95rem',
    fontWeight: 700,
    color: '#1a1a1a',
  },
  historyList: {
    overflowY: 'auto',
    padding: '0.75rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem',
  },
  historyItem: {
    display: 'flex',
    height: '34px',
    borderRadius: '6px',
    overflow: 'hidden',
    cursor: 'pointer',
    border: '2px solid transparent',
    transition: 'border-color 0.15s ease, transform 0.15s ease',
    '&:hover': { transform: 'translateY(-1px)' },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  historyItemActive: {
    borderColor: '#1976d2',
  },
  historySwatch: { flex: 1 },

  // Right-hand "Your palettes" browse drawer.
  browsePanel: {
    width: '290px',
    maxWidth: '85vw',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  browseList: {
    overflowY: 'auto',
    padding: '0.5rem 0.75rem 1rem',
  },
  browseEmpty: {
    padding: '1.5rem 0.75rem',
    color: '#777',
    fontSize: '0.9rem',
    lineHeight: 1.5,
  },
  browseSection: {
    fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#999',
    margin: '0.9rem 0 0.4rem',
  },
  browseItem: {
    display: 'block',
    width: '100%',
    border: 'none',
    background: 'none',
    padding: '0.4rem 0',
    cursor: 'pointer',
    textAlign: 'left',
  },
  browseStrip: {
    display: 'flex',
    height: '34px',
    borderRadius: '6px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
  },
  browseName: {
    display: 'block',
    fontSize: '0.8rem',
    color: '#333',
    marginTop: '0.25rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

export default generatorStyles;
